import { Box, Typography } from '@mui/material';
import { applyOpacity, periverseTheme, themeColors } from '@peritasai/ui-shared';
import {
  createFileRoute,
  Link,
  Outlet,
  redirect,
} from '@tanstack/react-router';

const adminRootSx = {
  minHeight: '100%',
  backgroundColor: themeColors.peritaspurple[50],
  borderLeft: `4px solid ${periverseTheme.palette.primary.main}`,
  borderTopRightRadius: '4px',
  borderBottomRightRadius: '4px',
};

const adminHeaderSx = {
  backgroundColor: periverseTheme.palette.primary.main,
  color: periverseTheme.palette.primary.contrastText,
  px: 3,
  py: 2,
  borderTopRightRadius: '4px',
};

const adminNavSx = {
  display: 'flex',
  gap: 3,
  px: 3,
  py: 2,
  backgroundColor: periverseTheme.palette.background.paper,
  borderBottom: `1px solid ${applyOpacity(periverseTheme.palette.primary.main, 0.2)}`,
};

const adminNavLinkSx = {
  color: periverseTheme.palette.primary.main,
  px: 1.5,
  py: 1,
  borderRadius: '6px',
  fontSize: '0.875rem',
  fontWeight: 500,
  textDecoration: 'none',
  transition: 'color 150ms, background-color 150ms',
  '&:hover': { color: periverseTheme.palette.primary.dark },
  '&[data-status="active"]': {
    backgroundColor: themeColors.peritaspurple[50],
    color: periverseTheme.palette.primary.dark,
  },
};

export const Route = createFileRoute('/_auth/_admin')({
  beforeLoad: async ({ context }) => {
    // Get fresh auth state directly from the store since context might be stale
    const { useAuthStore } = await import(
      '../../../features/auth/stores/auth.store'
    );
    const authStore = useAuthStore.getState();

    console.log('Admin route beforeLoad (fresh state):', {
      isAuthenticated: authStore.isAuthenticated,
      isAdmin: authStore.isAdmin(),
      isLoading: authStore.isLoading,
      isInitialized: authStore.isInitialized,
    });

    // Auth should already be initialized by parent _auth route
    // Just check if we need to wait for initialization
    if (authStore.isLoading || !authStore.isInitialized) {
      console.log(
        'Admin route: Waiting for auth initialization from parent route'
      );
      return;
    }

    // Redirect to login if not authenticated
    if (!authStore.isAuthenticated) {
      console.log('Admin route: Not authenticated, redirecting to /');
      throw redirect({
        to: '/',
      });
    }

    // Redirect to 403 if not admin
    if (!authStore.isAdmin()) {
      console.log('Admin route: Not admin, redirecting to /403');
      throw redirect({ to: '/403' });
    }

    console.log('Admin route: Access granted');
  },
  component: AdminLayout,
});

function AdminLayout() {
  // Authentication and role checks are now handled by beforeLoad hook
  // This component only renders when user is authenticated and is admin

  return (
    <Box sx={adminRootSx}>
      <Box sx={adminHeaderSx}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Admin Panel
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.8 }}>
          Administrative functions and settings
        </Typography>
      </Box>

      <Box component="nav" sx={adminNavSx}>
        <Box component={Link} to="/user-roles" sx={adminNavLinkSx}>
          User Roles
        </Box>
        <Box component={Link} to="/tracker-setup" sx={adminNavLinkSx}>
          Tracker Setup
        </Box>
        <Box component={Link} to="/cobot-setup" sx={adminNavLinkSx}>
          Cobot Setup
        </Box>
      </Box>

      <Box sx={{ p: 3, backgroundColor: periverseTheme.palette.background.paper }}>
        <Outlet />
      </Box>
    </Box>
  );
}
